import { useEffect, useState } from "react";
import { Coffee, Heart, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { getScanCount, markDismissed, shouldShowSupportPopup } from "../lib/scanCounter";

const SPONSOR_URL = "https://buymeacoffee.com/xbow";

/**
 * Mounted once at the app root. Listens for the `cve-scan-complete` custom
 * event (dispatched by Dashboard after each successful analyze) and decides
 * whether to open the dialog. Tracking + cooldown lives in localStorage.
 */
export default function SupportPopupHost() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const onScan = () => {
      if (shouldShowSupportPopup()) {
        setCount(getScanCount());
        setOpen(true);
      }
    };
    window.addEventListener("cve-scan-complete", onScan);
    return () => window.removeEventListener("cve-scan-complete", onScan);
  }, []);

  const close = (didDismiss = true) => {
    if (didDismiss) markDismissed();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close(true))}>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 sm:max-w-md"
        data-testid="support-popup"
      >
        <DialogHeader>
          <div className="h-10 w-10 rounded-md bg-amber-50 border border-amber-200 grid place-items-center mb-3">
            <Heart className="h-5 w-5 text-amber-700" />
          </div>
          <DialogTitle className="font-display text-xl leading-tight">
            You have run <span className="text-amber-700">{count} analyses</span> on us.
          </DialogTitle>
          <DialogDescription className="text-slate-700 text-sm leading-relaxed">
            Each scan costs real LLM tokens and OSV bandwidth. If this tool
            has saved you time, support this project so it stays free for the
            next defender after you.
          </DialogDescription>
        </DialogHeader>

        <ul className="text-xs text-slate-600 space-y-1.5 my-3">
          <li className="flex gap-2"><span className="text-amber-700">.</span>One-tap via Buy Me a Coffee, no signup</li>
          <li className="flex gap-2"><span className="text-amber-700">.</span>Goes straight to compute and new features</li>
          <li className="flex gap-2"><span className="text-amber-700">.</span>This dialog will not bother you again for two weeks</li>
        </ul>

        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <a
            href={SPONSOR_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => close(true)}
            data-testid="support-popup-cta"
            className="flex-1 flex items-center justify-center gap-2 bg-amber-300 hover:bg-amber-200 text-slate-900 font-semibold px-4 py-2.5 rounded-md transition-colors"
          >
            <Coffee className="h-4 w-4" />
            Support this project
          </a>
          <button
            onClick={() => close(true)}
            data-testid="support-popup-dismiss"
            className="flex items-center justify-center gap-1.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-md px-4 py-2.5 transition-colors"
          >
            <X className="h-4 w-4" />
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
